import {
  ChevronDownIcon,
  PencilIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { Button, Tooltip } from "@heroui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CountryFlag from "../../components/common/countryFlag";
import StripeAccountDetailsModal from "../../components/stripe/stripeAccountDetailsModal";
import {
  CommonProfileDto,
  DealResponseDto,
  DisplayMatchup,
  MatchupStatus,
} from "../../types/types";

interface MatchupCardProps {
  matchup: DisplayMatchup;
  boutNumber?: number;
  onCreateOpportunity?: () => void;
  onEdit?: (matchup: DisplayMatchup) => void;
  onDelete?: (matchup: DisplayMatchup) => void;
  onViewOpportunity?: (matchup: DisplayMatchup) => void;
  onViewDeal?: (matchup: DisplayMatchup) => void;
  onContact?: (fighterId: string | number) => void;

  isEventOwner?: boolean;
  canCreateDeal?: boolean;
  connectStatus?: string | null;
  connectStatusLoading?: boolean;
  onConnectAccount?: () => void;
}

const statusBadgeStyles: Record<string, { bg: string; text: string; border: string }> = {
  DRAFT: {
    bg: "bg-white",
    text: "text-slate-700",
    border: "border-slate-200",
  },
  OPPORTUNITY_POSTED: {
    bg: "bg-amber-100",
    text: "text-amber-800",
    border: "border-amber-200",
  },
  RED_ACCEPTED: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
  },
  BLUE_ACCEPTED: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
  },
  BOTH_ACCEPTED: {
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200",
  },
  CONFIRMED: {
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200",
  },
  CANCELLED: {
    bg: "bg-rose-100",
    text: "text-rose-800",
    border: "border-rose-200",
  },
};

const dealStatusBadgeStyles: Record<string, { bg: string; text: string; border: string }> = {
  NO_DEAL: {
    bg: "bg-white",
    text: "text-slate-700",
    border: "border-slate-200",
  },
  PROPOSED: {
    bg: "bg-amber-100",
    text: "text-amber-800",
    border: "border-amber-200",
  },
  ACCEPTED: {
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200",
  },
  CONFIRMED: {
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200",
  },
  WAITING_FOR_RESULT: {
    bg: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-200",
  },
  RESULT_UPDATED: {
    bg: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-200",
  },
  RESULT_CONFIRMED: {
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200",
  },
  PRE_PAYMENT_RELEASED: {
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200",
  },
  POST_PAYMENT: {
    bg: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-200",
  },
  POST_PAYMENT_RELEASED: {
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-200",
  },
  FINISHED: {
    bg: "bg-slate-900",
    text: "text-white",
    border: "border-slate-900",
  },
  CANCELLED: {
    bg: "bg-rose-100",
    text: "text-rose-800",
    border: "border-rose-200",
  },
  REJECTED: {
    bg: "bg-rose-100",
    text: "text-rose-800",
    border: "border-rose-200",
  },
};

const getFighterProfile = (
  fighter?: CommonProfileDto | string | null,
  fallbackProfile?: CommonProfileDto
): CommonProfileDto | undefined => {
  if (fighter && typeof fighter === "object") {
    return fighter;
  }
  return fallbackProfile;
};

const getFighterName = (
  fighter: CommonProfileDto | string | null | undefined,
  manualName: string | null | undefined,
  fallbackProfile: CommonProfileDto | undefined,
  tbdLabel: string
): string => {
  if (fighter && typeof fighter === "object") {
    const firstName = fighter.firstName || "";
    const lastName = fighter.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName) return fullName;
    if (fighter.username) return fighter.username;
  }

  if (typeof fighter === "string" && fighter.trim()) {
    return fighter.trim();
  }

  if (manualName && manualName.trim()) {
    return manualName.trim();
  }

  if (fallbackProfile) {
    const firstName = fallbackProfile.firstName || "";
    const lastName = fallbackProfile.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName) return fullName;
    if (fallbackProfile.username) return fallbackProfile.username;
  }

  return tbdLabel;
};

const getFighterCountry = (
  fighter?: CommonProfileDto | string | null,
  fallbackProfile?: CommonProfileDto
): string | undefined => {
  if (fighter && typeof fighter === "object") {
    const fAny = fighter as any;
    return (
      fAny.country ||
      fAny.fighterDto?.country ||
      fAny.residenceCountry ||
      fAny.region
    );
  }
  if (fallbackProfile) {
    const fbAny = fallbackProfile as any;
    return (
      fbAny.country ||
      fbAny.fighterDto?.country ||
      fbAny.residenceCountry ||
      fbAny.region
    );
  }
  return undefined;
};

const formatLabel = (value: string): string => {
  const text = value.replace(/_/g, " ").toLowerCase();
  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatCreatedAt = (
  value: string | undefined,
  fallbackLabel: string,
  locale?: string
): string => {
  if (!value) return fallbackLabel;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallbackLabel;

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
  };

  try {
    return date.toLocaleDateString(locale || undefined, dateOptions);
  } catch {
    return date.toLocaleDateString(undefined, dateOptions);
  }
};

const MatchupCard = ({
  matchup,
  onCreateOpportunity,
  onEdit,
  onDelete,
  onViewOpportunity,
  onViewDeal,
  onContact,
  isEventOwner,
  canCreateDeal,
  connectStatus,
  connectStatusLoading,
  onConnectAccount,
}: MatchupCardProps) => {
  const { t, i18n } = useTranslation("events");
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const currentStatus = String(matchup.status || "DRAFT").toUpperCase();
  const isCancelled = currentStatus === MatchupStatus.CANCELLED;

  const isOpportunityPublished =
    String(matchup.opportunityCardStatus || "").toUpperCase() === "PUBLISH";

  let hasCreateDealPermission = Boolean(isEventOwner);

  if (canCreateDeal !== undefined) {
    hasCreateDealPermission = canCreateDeal;
  }

  const canCreateDealForMatchup =
    hasCreateDealPermission &&
    Boolean(matchup.opportunityCardId) &&
    isOpportunityPublished &&
    !isCancelled;

  const canViewDeal = Boolean(isEventOwner) && Boolean(matchup.opportunityCardId);
  const createdAt = formatCreatedAt(
    matchup.createdAt,
    "/",
    i18n.language
  );

  const tbdLabel = t("tbd", "TBD");

  const redProfile = getFighterProfile(matchup.redCornerFighter, matchup.fighterOne);
  const blueProfile = getFighterProfile(matchup.blueCornerFighter, matchup.fighterTwo);

  const redFighterName = getFighterName(
    matchup.redCornerFighter,
    matchup.redCornerFighterName,
    matchup.fighterOne,
    tbdLabel
  );

  const blueFighterName = getFighterName(
    matchup.blueCornerFighter,
    matchup.blueCornerFighterName,
    matchup.fighterTwo,
    tbdLabel
  );

  const blueCountry = getFighterCountry(matchup.blueCornerFighter, matchup.fighterTwo);
  const redCountry = getFighterCountry(matchup.redCornerFighter, matchup.fighterOne);

  const weightCategory =
    (matchup as any).weightClass ||
    (matchup as any).weightCategory ||
    (matchup as any).division ||
    "Strawweight";

  const combatSport =
    (matchup as any).combatSport ||
    (matchup as any).sport ||
    (matchup as any).discipline ||
    "Boxing";

  const ruleset =
    (matchup as any).ruleset ||
    (matchup as any).rules ||
    (matchup as any).commission ||
    "IMMAF ruleset";

  const openProfile = (profileId: string | number) => {
    navigate(`/profile?profileId=${profileId}`);
  };

  const statusStyle = statusBadgeStyles[currentStatus] || statusBadgeStyles.DRAFT;

  const renderOpportunityAction = () => {
    if (currentStatus !== MatchupStatus.DRAFT || !onCreateOpportunity) {
      return null;
    }

    if (connectStatus === "VERIFICATION_PENDING") {
      return (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            color="primary"
            className="h-8 rounded-xl px-3 text-xs font-semibold shadow-none"
            isDisabled
          >
            {t("account_verification_pending", "Account Verification Pending")}
          </Button>
          <StripeAccountDetailsModal />
        </div>
      );
    }

    if (connectStatus === "ACCOUNT_ACTIVE") {
      return (
        <Button
          size="sm"
          color="primary"
          className="h-8 rounded-xl bg-blue-600 px-3 text-xs font-semibold text-white shadow-none"
          onPress={onCreateOpportunity}
        >
          {t("create_opportunity", "Create Opportunity")}
        </Button>
      );
    }

    return (
      <Button
        size="sm"
        color="secondary"
        className="h-8 rounded-xl px-3 text-xs font-semibold shadow-none"
        onPress={onConnectAccount}
        isLoading={connectStatusLoading}
        isDisabled={connectStatusLoading || !onConnectAccount}
      >
        {t("add_account", "Add Account")}
      </Button>
    );
  };

  const renderDealCard = (
    corner: "red" | "blue",
    fighterName: string,
    profile: CommonProfileDto | undefined,
    deal: DealResponseDto | null | undefined
  ) => {
    const isRedCorner = corner === "red";

    const isContacted = isRedCorner
      ? Boolean(matchup.redCornerContacted)
      : Boolean(matchup.blueCornerContacted);

    const isWaiting = isRedCorner
      ? Boolean(matchup.redCornerWaiting)
      : Boolean(matchup.blueCornerWaiting);

    const dealData: any = deal;

    let dealStatus = "NO_DEAL";
    let dealId = null;
    let amountText = null;

    if (dealData) {
      dealStatus = String(dealData.status || "UNKNOWN").toUpperCase();
      dealId = dealData.dealId || dealData.id || null;

      const core = dealData.formData?.core;

      if (core) {
        const amount = core.baseAmount;
        const currency = core.currency || "€";

        if (amount !== undefined && amount !== null) {
          amountText = `${amount} ${currency}`;
        }
      }
    }

    const badgeStyle =
      dealStatusBadgeStyles[dealStatus] ||
      dealStatusBadgeStyles.NO_DEAL;

    return (
      <div
        className={`w-full rounded-3xl p-4 flex flex-col justify-start gap-3 border ${
          isRedCorner
            ? "bg-rose-50/60 border-rose-200"
            : "bg-blue-50/60 border-blue-200"
        }`}
      >
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center rounded-xl px-2.5 py-0.5 text-xs font-medium border ${badgeStyle.bg} ${badgeStyle.text} ${badgeStyle.border}`}
          >
            {deal
              ? formatLabel(dealStatus)
              : t("no_deal", "No deal")}
          </span>
          {deal && amountText && (
            <span className="text-base font-bold text-slate-900 font-['Plus_Jakarta_Sans',sans-serif]">
              {amountText}
            </span>
          )}
        </div>

        {deal ? (
          <div className="flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-slate-500 font-medium font-['Plus_Jakarta_Sans',sans-serif]">
                {t("deal", "Deal:")}
              </span>
              <span className="font-bold text-slate-900 font-['Plus_Jakarta_Sans',sans-serif]">
                {dealId || `DL-${matchup.id || "000000"}`}
              </span>
            </div>
            {canViewDeal && dealData?.id && (
              <button
                type="button"
                onClick={() => navigate(`/deal/view/${dealData.id}`)}
                className="text-blue-600 font-medium hover:underline inline-flex items-center gap-1 text-sm font-['Plus_Jakarta_Sans',sans-serif]"
              >
                {t("view_deal", "View deal")}
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-slate-500">
              {t("deal_not_created", "No deal has been created for this fighter yet.")}
            </p>

            {profile?.id && canCreateDealForMatchup && (
              <div className="mt-1">
                {isWaiting ? (
                  <Button
                    size="sm"
                    variant="bordered"
                    className="h-7 rounded-xl border-slate-200 bg-slate-100 px-3 text-xs font-semibold text-slate-400 shadow-none"
                    isDisabled
                  >
                    {t("invite_pending", "Invite Pending")}
                  </Button>
                ) : !isContacted ? (
                  <Button
                    size="sm"
                    variant="bordered"
                    className="h-7 rounded-xl border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 shadow-none hover:bg-slate-50"
                    onPress={() => {
                      if (profile.id) {
                        onContact?.(profile.id);
                      }
                    }}
                    isDisabled={!onContact}
                  >
                    {t("contact", "Contact")}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="bordered"
                    className="h-7 rounded-xl border-blue-500 bg-white px-3 text-xs font-semibold text-blue-600 shadow-none hover:bg-blue-50"
                    onPress={() => navigate(`/chat?userId=${profile.id}`)}
                  >
                    {t("create_deal", "Create Deal")}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full overflow-hidden rounded-3xl border border-slate-200/90 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.03)] transition-all">

      <div className="lg:hidden">
        <div
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full cursor-pointer select-none transition-all px-4 py-2.5 bg-gradient-to-r from-blue-100 via-white via-40% to-rose-100 border border-slate-200/90 flex items-center justify-between gap-2 overflow-hidden ${
            isOpen
              ? "rounded-t-3xl border-b border-slate-200"
              : "rounded-3xl"
          }`}
        >
          <div className="flex-1 min-w-0 flex items-center gap-2.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (blueProfile?.id) openProfile(blueProfile.id);
              }}
              className="w-8 h-8 rounded-full shrink-0 overflow-hidden border border-blue-200 bg-white flex items-center justify-center text-blue-700 font-bold text-xs shadow-sm"
            >
              {blueProfile?.profileImage ? (
                <img
                  src={blueProfile.profileImage}
                  alt={blueFighterName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>
                  {(() => {
                    const f = blueProfile?.firstName?.trim();
                    const l = blueProfile?.lastName?.trim();
                    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
                    if (f) return f.slice(0, 2).toUpperCase();
                    const parts = blueFighterName.trim().split(/\s+/);
                    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                    return blueFighterName.slice(0, 2).toUpperCase();
                  })()}
                </span>
              )}
            </button>
            <div className="min-w-0 flex-1 flex flex-col justify-center">
              <span className="text-[11px] font-normal text-blue-600 leading-4 font-['Plus_Jakarta_Sans',sans-serif]">
                {t("blue_corner", "Blue corner")}
              </span>
              <div className="flex items-center gap-1 min-w-0">
                <span className="font-bold text-slate-900 text-xs leading-4 truncate font-['Plus_Jakarta_Sans',sans-serif]">
                  {blueFighterName}
                </span>
                {blueProfile?.verified && (
                  <CheckBadgeIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0 flex items-center justify-end gap-2.5 text-right">
            <div className="min-w-0 flex-1 flex flex-col justify-center text-right">
              <span className="text-[11px] font-normal text-rose-600 leading-4 font-['Plus_Jakarta_Sans',sans-serif]">
                {t("red_corner", "Red corner")}
              </span>
              <div className="flex items-center justify-end gap-1 min-w-0">
                {redProfile?.verified && (
                  <CheckBadgeIcon className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                )}
                <span className="font-bold text-slate-900 text-xs leading-4 truncate font-['Plus_Jakarta_Sans',sans-serif]">
                  {redFighterName}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (redProfile?.id) openProfile(redProfile.id);
              }}
              className="w-8 h-8 rounded-full shrink-0 overflow-hidden border border-rose-200 bg-white flex items-center justify-center text-rose-700 font-bold text-xs shadow-sm"
            >
              {redProfile?.profileImage ? (
                <img
                  src={redProfile.profileImage}
                  alt={redFighterName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>
                  {(() => {
                    const f = redProfile?.firstName?.trim();
                    const l = redProfile?.lastName?.trim();
                    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
                    if (f) return f.slice(0, 2).toUpperCase();
                    const parts = redFighterName.trim().split(/\s+/);
                    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                    return redFighterName.slice(0, 2).toUpperCase();
                  })()}
                </span>
              )}
            </button>
          </div>

          <div className="w-4 h-4 shrink-0 flex items-center justify-center ml-1">
            <ChevronDownIcon
              className={`w-4 h-4 text-slate-700 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>

        {isOpen && (
          <div className="p-4 flex flex-col gap-4 bg-white rounded-b-3xl border-x border-b border-slate-200/90">
            <div className="w-full flex items-center justify-between">
              <div className="w-8 h-6 flex items-center justify-start">
                {blueCountry ? (
                  <CountryFlag countryName={blueCountry} size={24} className="shadow-sm rounded-sm" />
                ) : (
                  <div className="w-7 h-4 bg-slate-100 border border-slate-200 rounded-sm" />
                )}
              </div>
              <span
                className={`inline-flex items-center rounded-xl px-2.5 py-0.5 text-xs font-medium border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}
              >
                {t(`matchup_status.${currentStatus}`, formatLabel(currentStatus))}
              </span>
              <div className="w-8 h-6 flex items-center justify-end">
                {redCountry ? (
                  <CountryFlag countryName={redCountry} size={24} className="shadow-sm rounded-sm" />
                ) : (
                  <div className="w-7 h-4 bg-slate-100 border border-slate-200 rounded-sm" />
                )}
              </div>
            </div>

            <div className="w-full flex flex-col items-center gap-2.5">
              <div className="w-full flex items-center justify-center">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="px-3 text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans',sans-serif]">
                  {t("versus", "Versus")}
                </span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
              <div className="w-full flex items-center justify-between text-slate-800 text-xs font-normal">
                <div className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-slate-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18m-9-6l9 3 9-3M3 9l9-3 9 3" />
                  </svg>
                  <span>{weightCategory}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-slate-600 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span>{combatSport}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-slate-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                  </svg>
                  <span>{ruleset}</span>
                </div>
              </div>
            </div>

            <div className="w-full flex items-center justify-between gap-2">
              <div>
                <h4 className="text-base sm:text-lg font-bold text-slate-900 font-['Plus_Jakarta_Sans',sans-serif]">
                  {t("fighter_details", "Fighter details")}
                </h4>
                <p className="text-xs text-slate-400 font-['Plus_Jakarta_Sans',sans-serif]">
                  {t("fighter_deal_status_description", "Deal status for each corner")}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                {onEdit && (
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => onEdit(matchup)}
                    className="h-8 w-8 min-w-8 rounded-2xl text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                    aria-label={t("edit_matchup", "Edit Matchup")}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                )}
                {onDelete && (
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => onDelete(matchup)}
                    className="h-8 w-8 min-w-8 rounded-2xl text-slate-500 hover:text-red-600 hover:bg-red-50"
                    aria-label={t("delete_matchup", "Delete Matchup")}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              {renderDealCard("blue", blueFighterName, blueProfile, matchup.blueCornerDeal)}
              {renderDealCard("red", redFighterName, redProfile, matchup.redCornerDeal)}
            </div>

            <div className="w-full bg-slate-50/80 border border-slate-200/80 rounded-3xl p-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-400 font-medium font-['Plus_Jakarta_Sans',sans-serif]">
                  {t("title_fight", "Title fight")}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900 font-['Plus_Jakarta_Sans',sans-serif]">
                  {matchup.titleFight ? t("yes", "Yes") : t("no", "No")}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium font-['Plus_Jakarta_Sans',sans-serif]">
                  {t("result", "Result")}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900 truncate font-['Plus_Jakarta_Sans',sans-serif]">
                  {matchup.resultType ? formatLabel(String(matchup.resultType)) : "/"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium font-['Plus_Jakarta_Sans',sans-serif]">
                  {t("opportunity_status", "Opportunity")}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900 truncate font-['Plus_Jakarta_Sans',sans-serif]">
                  {matchup.opportunityCardStatus ? formatLabel(String(matchup.opportunityCardStatus)) : "/"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium font-['Plus_Jakarta_Sans',sans-serif]">
                  {t("created", "Created")}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900 truncate font-['Plus_Jakarta_Sans',sans-serif]">
                  {createdAt}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              {matchup.opportunityCardId ? (
                <>
                  {onViewOpportunity && (
                    <Button
                      size="sm"
                      variant="bordered"
                      className="h-8 rounded-xl border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-none hover:bg-slate-50"
                      onPress={() => onViewOpportunity(matchup)}
                    >
                      {t("view_opportunity", "View Opportunity")}
                    </Button>
                  )}
                  {onViewDeal && (
                    <Button
                      size="sm"
                      variant="bordered"
                      className="h-8 rounded-xl border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-none hover:bg-slate-50"
                      onPress={() => onViewDeal(matchup)}
                    >
                      {t("view_deal", "View Deal")}
                    </Button>
                  )}
                </>
              ) : (
                renderOpportunityAction()
              )}
            </div>
          </div>
        )}
      </div>

      <div className="hidden lg:block">
        <div
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full cursor-pointer px-6 py-3.5 flex items-center justify-between gap-5 bg-gradient-to-r from-blue-50/80 via-white via-40% to-rose-50/80 transition-all select-none ${
            isOpen ? "rounded-t-3xl border-b border-slate-200/80" : "rounded-3xl"
          }`}
        >
          <div className="flex-1 flex items-center justify-start gap-3 min-w-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (blueProfile?.id) openProfile(blueProfile.id); }}
              className="w-10 h-10 rounded-full shrink-0 overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-700 font-bold text-sm shadow-sm"
            >
              {blueProfile?.profileImage ? (
                <img src={blueProfile.profileImage} alt={blueFighterName} className="w-full h-full object-cover" />
              ) : (
                <span>
                  {(() => {
                    const f = blueProfile?.firstName?.trim();
                    const l = blueProfile?.lastName?.trim();
                    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
                    if (f) return f.slice(0, 2).toUpperCase();
                    const parts = blueFighterName.trim().split(/\s+/);
                    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                    return blueFighterName.slice(0, 2).toUpperCase();
                  })()}
                </span>
              )}
            </button>

            <div className="min-w-0 flex flex-col justify-center">
              <span className="text-[11px] font-medium text-blue-600 leading-tight">{t("blue_corner", "Blue corner")}</span>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-bold text-slate-900 text-base truncate" title={blueFighterName}>{blueFighterName}</span>
                {blueProfile?.verified && <CheckBadgeIcon className="w-4 h-4 text-blue-500 shrink-0" />}
              </div>
            </div>

            {blueCountry && (
              <div className="flex items-center ml-1">
                <CountryFlag countryName={blueCountry} size={22} className="shadow-sm" />
              </div>
            )}
          </div>

          <div className="flex flex-col items-center justify-center gap-1.5 shrink-0 px-2">
            <span className={`inline-flex items-center rounded-xl px-2.5 py-0.5 text-[11px] font-medium border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
              {t(`matchup_status.${currentStatus}`, formatLabel(currentStatus))}
            </span>
            <div className="flex items-center gap-2 w-28">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">{t("versus", "Versus")}</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-700 font-normal">
              <div className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-slate-700 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18m-9-6l9 3 9-3M3 9l9-3 9 3" />
                </svg>
                <span>{weightCategory}</span>
              </div>
              <div className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-slate-700 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                <span>{combatSport}</span>
              </div>
              <div className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 text-slate-700 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
                <span>{ruleset}</span>
              </div>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-end gap-3 min-w-0">
            {redCountry && (
              <div className="flex items-center mr-1">
                <CountryFlag countryName={redCountry} size={22} className="shadow-sm" />
              </div>
            )}
            <div className="min-w-0 flex flex-col justify-center text-right">
              <span className="text-[11px] font-medium text-rose-600 leading-tight">{t("red_corner", "Red corner")}</span>
              <div className="flex items-center justify-end gap-1.5 min-w-0">
                <span className="font-bold text-slate-900 text-base truncate text-right" title={redFighterName}>{redFighterName}</span>
                {redProfile?.verified && <CheckBadgeIcon className="w-4 h-4 text-rose-500 shrink-0" />}
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (redProfile?.id) openProfile(redProfile.id); }}
              className="w-10 h-10 rounded-full shrink-0 overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-700 font-bold text-sm shadow-sm"
            >
              {redProfile?.profileImage ? (
                <img src={redProfile.profileImage} alt={redFighterName} className="w-full h-full object-cover" />
              ) : (
                <span>
                  {(() => {
                    const f = redProfile?.firstName?.trim();
                    const l = redProfile?.lastName?.trim();
                    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
                    if (f) return f.slice(0, 2).toUpperCase();
                    const parts = redFighterName.trim().split(/\s+/);
                    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                    return redFighterName.slice(0, 2).toUpperCase();
                  })()}
                </span>
              )}
            </button>
            <div className="ml-2">
              <ChevronDownIcon className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="p-6 flex flex-col gap-4 bg-white rounded-b-3xl">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h4 className="text-lg font-bold text-slate-900">{t("fighter_details", "Fighter details")}</h4>
                <p className="text-xs text-slate-400">{t("fighter_deal_status_description", "Deal status for each corner")}</p>
              </div>
              <div className="flex items-center gap-2">
                {onEdit && (
                  <Tooltip content={t("edit", "Edit")} closeDelay={0}>
                    <Button isIconOnly size="sm" variant="light" onPress={() => onEdit(matchup)}
                      className="h-8 w-8 min-w-8 rounded-2xl text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                      aria-label={t("edit_matchup", "Edit Matchup")}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                )}
                {onDelete && (
                  <Tooltip content={t("delete", "Delete")} color="danger" closeDelay={0}>
                    <Button isIconOnly size="sm" variant="light" onPress={() => onDelete(matchup)}
                      className="h-8 w-8 min-w-8 rounded-2xl text-slate-500 hover:text-red-600 hover:bg-red-50"
                      aria-label={t("delete_matchup", "Delete Matchup")}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderDealCard("blue", blueFighterName, blueProfile, matchup.blueCornerDeal)}
              {renderDealCard("red", redFighterName, redProfile, matchup.redCornerDeal)}
            </div>

            <div className="bg-slate-50/80 border border-slate-200/80 rounded-3xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-400 font-medium">{t("title_fight", "Title fight")}</p>
                <p className="mt-0.5 text-sm font-medium text-slate-900">{matchup.titleFight ? t("yes", "Yes") : t("no", "No")}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium">{t("result", "Result")}</p>
                <p className="mt-0.5 text-sm font-medium text-slate-900 truncate">{matchup.resultType ? formatLabel(String(matchup.resultType)) : "/"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium">{t("opportunity_status", "Opportunity")}</p>
                <p className="mt-0.5 text-sm font-medium text-slate-900 truncate">{matchup.opportunityCardStatus ? formatLabel(String(matchup.opportunityCardStatus)) : "/"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium">{t("created", "Created")}</p>
                <p className="mt-0.5 text-sm font-medium text-slate-900 truncate">{createdAt}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              {matchup.opportunityCardId ? (
                <>
                  {onViewOpportunity && (
                    <Button size="sm" variant="bordered"
                      className="h-8 rounded-xl border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-none hover:bg-slate-50"
                      onPress={() => onViewOpportunity(matchup)}
                    >
                      {t("view_opportunity", "View Opportunity")}
                    </Button>
                  )}
                  {onViewDeal && (
                    <Button size="sm" variant="bordered"
                      className="h-8 rounded-xl border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-none hover:bg-slate-50"
                      onPress={() => onViewDeal(matchup)}
                    >
                      {t("view_deal", "View Deal")}
                    </Button>
                  )}
                </>
              ) : (
                renderOpportunityAction()
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default MatchupCard;
